import React from 'react'
import { Alert, AlertIcon, AlertDescription, Box } from '@chakra-ui/react'
import { FaExclamationTriangle, FaExclamationCircle } from 'react-icons/fa'
import { Icon } from '@chakra-ui/icons'

import SharedStack from '~components/shared/wrappers/shared-stack'
import { MessageWarning } from './helpers'

interface Props {
    warnings: MessageWarning[]
}

/**
 * Displays warning/error messages when a client may not receive a message
 * due to unsubscription or disabled notification settings.
 */
export default function MessageWarningBanner({ warnings }: Props) {
    if (warnings.length === 0) return null

    // Group warnings by type for cleaner display
    const errors = warnings.filter(w => w.type === 'error')
    const warningMessages = warnings.filter(w => w.type === 'warning')

    return (
        <Box px={2} pt={2}>
            <SharedStack gap={2}>
                {errors.length > 0 && (
                    <Alert
                        status="error"
                        variant="subtle"
                        rounded="lg"
                        py={2}
                        px={3}
                    >
                        <Icon as={FaExclamationCircle} color="red.500" mr={2} />
                        <AlertDescription fontSize="sm">
                            <SharedStack gap={1}>
                                {errors.map((error, idx) => (
                                    <Box key={idx}>{error.message}</Box>
                                ))}
                            </SharedStack>
                        </AlertDescription>
                    </Alert>
                )}
                {warningMessages.length > 0 && (
                    <Alert
                        status="warning"
                        variant="subtle"
                        rounded="lg"
                        py={2}
                        px={3}
                    >
                        <Icon
                            as={FaExclamationTriangle}
                            color="orange.500"
                            mr={2}
                        />
                        <AlertDescription fontSize="sm">
                            <SharedStack gap={1}>
                                {warningMessages.map((warning, idx) => (
                                    <Box key={idx}>{warning.message}</Box>
                                ))}
                            </SharedStack>
                        </AlertDescription>
                    </Alert>
                )}
            </SharedStack>
        </Box>
    )
}
